import { useQuery } from "@tanstack/react-query";

import { withJWTClient } from "./connectClient";
import { CONNECT_API_V1_BASE_URL } from "./gateway";
import type { SiteId } from "@/types/dragon-tiles";

export type InfobipSmsConfig = {
  base_url: string;
  api_key_prefix: string | null;
  sender_id: string;
  entity_id: string | null;
  extra: Record<string, unknown>;
  create_date: string;
  modify_date: string;
};

// Types for the site response
export type Site = {
  id: string;
  account: number;
  url: string;
  timezone: string;
  currency: string;
  show_branding: boolean;
  tracking_links_enabled: boolean;
  google_analytics_id: string;
  google_analytics_is_active: boolean;
  suppressed_domains: string[];
  default_from_name: string;
  default_from_email: string;
  default_reply_to: string;
  email_ready: boolean | null;
  first_name_fields: string[];
  buffer: string;
  click_attribution: boolean;
  open_attribution: boolean;
  lookback_window: number;
  bcc_emails_enabled: boolean;
  bcc_email_address: string;
  recommendations_days_back: number;
  recommendations_ignored_products: string[];
  gdpr_enabled: boolean;
  gdpr_request_email: string;
  gdpr_results_email: string;
  frequency_cap_day_count: number;
  frequency_cap_week_count: number;
  frequency_cap_month_count: number;
  notification_emails: string[];
  notification_events: string[];
  preheader_styles: string;
  sender_domain: string;
  webhook_secret: string;
  two_step_unsubscribe: boolean;
  account_dynamic_segment_limit: number;
  sms_enabled: boolean;
  /** Backend may omit this field while RCS approval rollout is in progress. */
  rcs_enabled?: boolean;
  infobip_sms_config: InfobipSmsConfig | null;
  sms_click_attribution: boolean;
  sms_lookback_window: number;
  brand_name: string | null;
  sms_primary_phone_number: string | null;
  is_connect: boolean;
  test_mode: boolean;
  quiet_hour_start: string;
  quiet_hour_end: string;
  /** Send window bounds as minutes-of-day in the site's local time (null = unset). */
  send_window_start: number | null;
  send_window_end: number | null;
};

export type SiteResponse = Site;

/** True when SMS OTP can be used: sms enabled and Infobip sender_id is configured */
export function isOtpActive(
  site: Pick<Site, "sms_enabled" | "infobip_sms_config"> | null | undefined,
): boolean {
  if (!site) return false;
  return (
    site.sms_enabled === true &&
    !!site?.infobip_sms_config &&
    !!site.infobip_sms_config?.sender_id
  );
}

interface GatewayInfobipConfig {
  base_url: string;
  api_key_prefix: string;
  sender_id: string;
  entity_id: string;
  extra: Record<string, unknown> | null;
}

export interface GatewaySite {
  id?: string;
  rj_id?: string;
  account_id: number;
  url: string;
  timezone: string;
  currency: string;
  show_branding: boolean | null;
  tracking_links_enabled: boolean | null;
  google_analytics_id: string;
  google_analytics_is_active: boolean | null;
  suppressed_domains: string[];
  default_from_name: string;
  default_from_email: string;
  default_reply_to: string;
  // Server-computed (connect-api emailready); absent on APIs predating it.
  email_ready?: boolean | null;
  bcc_emails_enabled: boolean | null;
  bcc_email_address: string;
  recommendations_days_back: number;
  recommendations_ignored_products: string[];
  recommendations_ignored_categories: string[];
  gdpr_enabled: boolean | null;
  gdpr_request_email: string;
  gdpr_results_email: string;
  frequency_cap_day_count: number;
  frequency_cap_week_count: number;
  frequency_cap_month_count: number;
  preheader_styles: string;
  sender_domain: string;
  two_step_unsubscribe: boolean | null;
  sms_enabled: boolean | null;
  brand_name: string;
  sms_primary_phone_number: string;
  is_connect: boolean | null;
  test_mode?: boolean | null;
  text_provider_config: { infobip?: GatewayInfobipConfig | null } | null;
  sms_quiet_hour_start: number | null;
  sms_quiet_hour_end: number | null;
  // Send window replaces RJ2 quiet hours; minutes-of-day, may be absent mid-rollout.
  sms_send_window_start?: number | null;
  sms_send_window_end?: number | null;
}

// rj2 serves quiet hours as full ISO datetimes and the UI only reads the UTC
// hour out of them; the proto carries the bare hour, so we re-anchor it.
function quietHourToIso(hour: number | null): string {
  if (hour === null) return "";
  return `1970-01-01T${String(hour).padStart(2, "0")}:00:00.000Z`;
}

function fromGatewayInfobipConfig(
  config: GatewayInfobipConfig,
): InfobipSmsConfig {
  return {
    base_url: config.base_url,
    api_key_prefix: config.api_key_prefix || null,
    sender_id: config.sender_id,
    entity_id: config.entity_id || null,
    extra: config.extra ?? {},
    create_date: "",
    modify_date: "",
  };
}

// Fields absent from the Site proto (first_name_fields, buffer, attribution
// windows, notification_*, webhook_secret, account_dynamic_segment_limit,
// sms_click_attribution, sms_lookback_window) get type-compatible defaults;
// no current consumer of this module reads them.
export function fromGatewaySite(data: GatewaySite): Site {
  const infobip = data.text_provider_config?.infobip;
  return {
    id: data.rj_id || data.id || "",
    account: data.account_id,
    url: data.url,
    timezone: data.timezone,
    currency: data.currency,
    show_branding: data.show_branding ?? false,
    tracking_links_enabled: data.tracking_links_enabled ?? false,
    google_analytics_id: data.google_analytics_id,
    google_analytics_is_active: data.google_analytics_is_active ?? false,
    suppressed_domains: data.suppressed_domains,
    default_from_name: data.default_from_name,
    default_from_email: data.default_from_email,
    default_reply_to: data.default_reply_to,
    email_ready: data.email_ready ?? null,
    first_name_fields: [],
    buffer: "",
    click_attribution: false,
    open_attribution: false,
    lookback_window: 0,
    bcc_emails_enabled: data.bcc_emails_enabled ?? false,
    bcc_email_address: data.bcc_email_address,
    recommendations_days_back: data.recommendations_days_back,
    recommendations_ignored_products: data.recommendations_ignored_products,
    gdpr_enabled: data.gdpr_enabled ?? false,
    gdpr_request_email: data.gdpr_request_email,
    gdpr_results_email: data.gdpr_results_email,
    frequency_cap_day_count: data.frequency_cap_day_count,
    frequency_cap_week_count: data.frequency_cap_week_count,
    frequency_cap_month_count: data.frequency_cap_month_count,
    notification_emails: [],
    notification_events: [],
    preheader_styles: data.preheader_styles,
    sender_domain: data.sender_domain,
    webhook_secret: "",
    two_step_unsubscribe: data.two_step_unsubscribe ?? false,
    account_dynamic_segment_limit: 0,
    sms_enabled: data.sms_enabled ?? false,
    infobip_sms_config: infobip ? fromGatewayInfobipConfig(infobip) : null,
    sms_click_attribution: false,
    sms_lookback_window: 0,
    brand_name: data.brand_name || null,
    sms_primary_phone_number: data.sms_primary_phone_number || null,
    is_connect: data.is_connect ?? false,
    // Unset means ON: connect-api reads a NULL TestMode as true (models/site.go).
    test_mode: data.test_mode ?? true,
    quiet_hour_start: quietHourToIso(data.sms_quiet_hour_start),
    quiet_hour_end: quietHourToIso(data.sms_quiet_hour_end),
    send_window_start: data.sms_send_window_start ?? null,
    send_window_end: data.sms_send_window_end ?? null,
  };
}

export const accountKeys = {
  all: () => ["account"],
  site: (siteId: string) => ["account", "site", siteId],
};

export async function getSite(siteId: SiteId): Promise<SiteResponse> {
  // connect-api #247 made GET /v1/sites/{id} return 400 (FailedPrecondition)
  // for inactive-account sites unless accept_inactive=true. This is the app-wide
  // fetch for the currently-selected site (composer, previews, frame editors,
  // etc.); it must keep loading the site regardless of account standing, so we
  // always opt in.
  const query = new URLSearchParams({ accept_inactive: "true" });
  const response = await withJWTClient((client) =>
    client.get<GatewaySite>(
      `${CONNECT_API_V1_BASE_URL}/sites/${encodeURIComponent(siteId)}?${query.toString()}`,
    ),
  );
  return fromGatewaySite(response.data);
}

export function useAccountSite(siteId: string) {
  return useQuery({
    queryKey: accountKeys.site(siteId),
    queryFn: () => getSite(siteId),
    enabled: !!siteId,
  });
}

// Query options for use in other components
export function siteQueryOptions(siteId: string) {
  return {
    queryKey: accountKeys.site(siteId),
    queryFn: () => getSite(siteId),
  };
}
